/**
 * MomentumConditionCards — Power Trend Condition Status Panel
 *
 * Compact bar rendered above the chart (inside ChartPage) when the
 * MOMENTUM_OVERLAY indicator is active.  Reads the shared
 * window.__momentum_state that calc() writes on every bar recalculation.
 *
 * Layout:
 *   [C1 card] [C2 card] [C3 card] [C4 card] | [Status badge + Stop level]
 *
 * C1/C2: streak-based progress bar (current streak / threshold)
 * C3/C4: boolean filled / empty indicator
 */

import { useEffect, useState } from 'react';
import { useChartStore } from '@/store/chartStore';
import type { MomentumState } from '@/indicators/momentumOverlay';

// ── Snapshot type ─────────────────────────────────────────────────────────────

type Snapshot = Pick<
  MomentumState,
  'inTrend' | 'c1Streak' | 'c2Streak' | 'c1Met' | 'c2Met' | 'c3Met' | 'c4Green' | 'stopLevel'
>;

const DEFAULT: Snapshot = {
  inTrend: false, c1Streak: 0, c2Streak: 0,
  c1Met: false, c2Met: false, c3Met: false, c4Green: false,
  stopLevel: null,
};

function readState(): Snapshot {
  const s = window.__momentum_state;
  if (!s) return DEFAULT;
  return {
    inTrend:  s.inTrend,
    c1Streak: s.c1Streak,
    c2Streak: s.c2Streak,
    c1Met:    s.c1Met,
    c2Met:    s.c2Met,
    c3Met:    s.c3Met,
    c4Green:  s.c4Green,
    stopLevel: s.stopLevel,
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

export function MomentumConditionCards() {
  const activeIndicatorNames = useChartStore((s) => s.activeIndicatorNames);
  const lastBar              = useChartStore((s) => s.lastBar);
  const isActive             = activeIndicatorNames.includes('MOMENTUM_OVERLAY');

  const [snap, setSnap] = useState<Snapshot>(DEFAULT);

  useEffect(() => {
    if (isActive) setSnap(readState());
    else setSnap(DEFAULT);
  }, [lastBar, isActive]);

  if (!isActive) return null;

  const { inTrend, c1Streak, c2Streak, c1Met, c2Met, c3Met, c4Green, stopLevel } = snap;

  return (
    <div className="flex flex-row items-stretch gap-2 px-3 py-1.5 border-b border-border bg-panel shrink-0 overflow-x-auto">

      {/* ── C1 ─────────────────────────────────────────────────────────── */}
      <StreakCard
        label="Low > EMA21"
        streak={c1Streak}
        threshold={10}
        met={c1Met}
      />

      {/* ── C2 ─────────────────────────────────────────────────────────── */}
      <StreakCard
        label="EMA21 > SMA50"
        streak={c2Streak}
        threshold={5}
        met={c2Met}
      />

      {/* ── C3 ─────────────────────────────────────────────────────────── */}
      <BoolCard label="SMA50 Uptrend" met={c3Met} />

      {/* ── C4 ─────────────────────────────────────────────────────────── */}
      <BoolCard label="Strong Close" met={c4Green} />

      {/* ── Divider ────────────────────────────────────────────────────── */}
      <div className="w-px bg-border self-stretch mx-1 shrink-0" />

      {/* ── Status badge + stop level ───────────────────────────────────── */}
      <div className="flex flex-col justify-center items-end gap-0.5 min-w-[130px] shrink-0">
        <div
          className={`flex items-center gap-1.5 text-[11px] font-bold tracking-wide ${
            inTrend ? 'text-green-400' : 'text-slate-500'
          }`}
        >
          <span className="text-[7px]">{inTrend ? '⬤' : '○'}</span>
          <span>{inTrend ? 'POWER TREND' : 'WATCHING'}</span>
        </div>
        {inTrend && stopLevel !== null && (
          <div className="text-[10px] text-amber-400 font-mono">
            Stop: {stopLevel.toFixed(2)}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

interface StreakCardProps {
  label:     string;
  streak:    number;
  threshold: number;
  met:       boolean;
}

function StreakCard({ label, streak, threshold, met }: StreakCardProps) {
  const pct = Math.min(streak / threshold, 1);
  const colorCls  = met ? 'text-green-400' : streak >= threshold * 0.5 ? 'text-amber-400' : 'text-slate-500';
  const barCls    = met ? 'bg-green-500'   : streak >= threshold * 0.5 ? 'bg-amber-500'   : 'bg-slate-600';

  return (
    <div className="flex flex-col justify-center gap-1 flex-1 min-w-[80px]">
      <div className={`text-[10px] font-medium leading-none ${colorCls}`}>{label}</div>
      <div className="flex items-center gap-1.5">
        <div className="flex-1 h-1.5 bg-gray-700 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-[width] duration-300 ${barCls}`}
            style={{ width: `${pct * 100}%` }}
          />
        </div>
        <span className={`text-[10px] font-mono tabular-nums shrink-0 ${colorCls}`}>
          {streak}/{threshold}
        </span>
      </div>
    </div>
  );
}

interface BoolCardProps {
  label: string;
  met:   boolean;
}

function BoolCard({ label, met }: BoolCardProps) {
  const colorCls = met ? 'text-green-400' : 'text-slate-500';
  const barCls   = met ? 'bg-green-500'   : 'bg-slate-700';

  return (
    <div className="flex flex-col justify-center gap-1 flex-1 min-w-[70px]">
      <div className={`text-[10px] font-medium leading-none ${colorCls}`}>{label}</div>
      <div className="flex items-center gap-1.5">
        <div className="flex-1 h-1.5 bg-gray-700 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-[width] duration-300 ${barCls} ${met ? 'w-full' : 'w-0'}`}
          />
        </div>
        <span className={`text-[10px] font-mono shrink-0 ${colorCls}`}>{met ? '✓' : '—'}</span>
      </div>
    </div>
  );
}
